import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FAQSlideContent } from '@/types/course-outline';
import { useState } from 'react';
import SlideShell from './SlideShell';
import MarkdownRenderer from './MarkdownRenderer';

interface FAQSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: FAQSlideContent;
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const FAQSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: FAQSlideProps) => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={!!onMarkComplete}
      isCompleted={isCompleted}
      onMarkComplete={onMarkComplete}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      <div className="space-y-3">
        {content.questions.map((item, index) => (
          <Collapsible
            key={index}
            open={openItems.includes(index)}
            onOpenChange={() => toggleItem(index)}
          >
            <CollapsibleTrigger className="w-full">
              <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-4 hover:bg-[#fafafa] transition-colors text-left">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-foreground pr-2">
                    <MarkdownRenderer content={item.question} />
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${
                      openItems.includes(index) ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 bg-white rounded-[15px] border border-[#f0f0f0] p-4">
                <MarkdownRenderer content={item.answer} />
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </SlideShell>
  );
};

export default FAQSlide;
